/**
 * The Sheets section (CLAUDE.md 1.3, 21).
 *
 * Its own destination in the navigation, never inside the module catalog and never
 * a peer of a verified module in a picker. The banner states the firewall once,
 * calmly, where the section begins -- a sheet titled "EC2 Beam Design" sitting at
 * the same visual weight as a verified module will otherwise be assumed to carry
 * the same assurance, and it does not.
 *
 * Sheets are server-side now, so this reads through TanStack Query, which owns all
 * server state (14.1).
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  FileSpreadsheet,
  LayoutTemplate,
  Lock,
  Plus,
  Search,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { WorkspaceChrome } from '@/components/layout/WorkspaceChrome'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { FullPageSpinner } from '@/components/layout/FullPageSpinner'
import { formatRelative } from '@/lib/format'
import { createSheet, deleteSheet, listSheets } from '@/sheets/api'
import { TemplateGallery } from '@/sheets/components/TemplateGallery'
import type { SheetDoc } from '@/sheets/model/types'
import { emptySheetDocument } from '@/sheets/useSheet'

export function SheetsPage() {
  const { t, i18n } = useTranslation(['sheets', 'common'])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [galleryOpen, setGalleryOpen] = useState(false)

  const sheets = useQuery({
    queryKey: ['sheets'],
    queryFn: ({ signal }) => listSheets(signal),
  })

  // One create path for a blank sheet and for a template: whatever the document
  // came from, the sheet is the user's own (ADR-0013 rule 3).
  const create = useMutation({
    mutationFn: (document: SheetDoc) => createSheet({ title: document.title, document }),
    onSuccess: (sheet) => {
      void queryClient.invalidateQueries({ queryKey: ['sheets'] })
      navigate(`/sheets/${sheet.id}`)
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteSheet(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sheets'] }),
  })

  const rows = useMemo(() => {
    const all = sheets.data ?? []
    const needle = query.trim().toLowerCase()
    return needle ? all.filter((row) => (row.title || '').toLowerCase().includes(needle)) : all
  }, [query, sheets.data])

  return (
    <WorkspaceChrome
      title={t('sheets:sheets.title')}
      actions={
        <div className="flex items-center gap-2">
          {/* The fast path stays one click: a blank sheet, no gallery in the way. */}
          <Button variant="ghost" onClick={() => setGalleryOpen(true)} data-testid="from-template">
            <LayoutTemplate className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">{t('sheets:sheets.templates.from_template')}</span>
          </Button>
          <Button onClick={() => create.mutate(emptySheetDocument())} loading={create.isPending}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('sheets:sheets.new')}
          </Button>
        </div>
      }
    >
      <div className="mx-auto w-full max-w-5xl p-4 sm:p-6">
        <p className="mb-4 max-w-2xl text-sm leading-relaxed text-text-muted">
          {t('sheets:sheets.subtitle')}
        </p>

        {/* The firewall, stated once, where the section begins (1.3). */}
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-[var(--status-warn)] bg-[var(--status-warn-bg)] p-4">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-[var(--status-warn)]"
            aria-hidden
          />
          <div>
            <p className="text-sm font-semibold text-[var(--status-warn)]">
              {t('sheets:sheets.unverified.title')}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-text">
              {t('sheets:sheets.unverified.body')}
            </p>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-full max-w-sm items-center gap-2 rounded-md border border-border bg-surface px-3">
            <Search className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label={t('sheets:sheets.search')}
              placeholder={t('sheets:sheets.search')}
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-text placeholder:text-text-muted focus:outline-none"
            />
          </div>
        </div>

        {sheets.isLoading ? (
          <FullPageSpinner />
        ) : rows.length === 0 ? (
          <Card>
            <CardBody className="flex flex-col items-center gap-3 py-12 text-center">
              <FileSpreadsheet className="h-8 w-8 text-text-muted" aria-hidden />
              <p className="text-sm font-semibold">{t('sheets:sheets.empty.title')}</p>
              <p className="max-w-md text-sm text-text-muted">{t('sheets:sheets.empty.body')}</p>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                <Button
                  onClick={() => create.mutate(emptySheetDocument())}
                  loading={create.isPending}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  {t('sheets:sheets.new')}
                </Button>
                <Button variant="ghost" onClick={() => setGalleryOpen(true)}>
                  <LayoutTemplate className="h-4 w-4" aria-hidden />
                  {t('sheets:sheets.templates.from_template')}
                </Button>
              </div>
            </CardBody>
          </Card>
        ) : (
          <Card>
            <ul>
              {rows.map((sheet) => (
                <li
                  key={sheet.id}
                  className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/sheets/${sheet.id}`)}
                    className="min-w-0 flex-1 text-start"
                  >
                    <span className="block truncate text-sm font-medium">
                      {sheet.title || t('sheets:sheets.untitled')}
                    </span>
                    <span className="mt-0.5 block text-xs text-text-muted">
                      {t('sheets:sheets.count.cells', { count: sheet.cell_count })}
                    </span>
                  </button>
                  <span className="hidden text-xs text-text-muted sm:inline">
                    {formatRelative(sheet.updated_at ?? sheet.created_at, i18n.language)}
                  </span>
                  {sheet.can_edit && (
                    <button
                      type="button"
                      onClick={() => remove.mutate(sheet.id)}
                      aria-label={t('common:common.delete')}
                      className="flex h-11 w-11 items-center justify-center text-text-muted hover:text-[var(--status-fail)]"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}

        <p className="mt-4 flex items-center gap-2 text-xs text-text-muted">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          {t('sheets:sheets.private_note')}
        </p>
      </div>

      <TemplateGallery
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onPick={(document) => {
          setGalleryOpen(false)
          create.mutate(document)
        }}
      />
    </WorkspaceChrome>
  )
}
